<script setup lang="ts">
import { COMMERCE_TYPE } from "~/constants";
import useEventStore from "~/stores/event";
import { getAccessLabels, getDurationLabel, getPriceLabel, getReleaseDateTitle } from "~/utils/subscription";

export interface CommerceOptionsProps {
  disabled?: boolean;
}

const { t } = useI18n();
const props = defineProps<CommerceOptionsProps>();
const emit = defineEmits(["buyCommerce"]);

const eventStore = useEventStore();
const appConfig = useRuntimeConfig().public;

const hasPayments = computed(() => appConfig.APP_FLAGS.hasPayments);
const disabledCommerce = computed(() => !hasPayments.value || props.disabled);
const onClickBuyCommerce = (commerceOption: IModelCommerce) => emit("buyCommerce", commerceOption);
const availableTickets = computed(() => eventStore.commerceOptions.filter(ticket => ticket.type === COMMERCE_TYPE.PPV));
const availableSubscriptions = computed(() => eventStore.commerceOptions
  .filter(subscription => subscription.type === COMMERCE_TYPE.SVOD || subscription.type === COMMERCE_TYPE.CVOD));
</script>

<template>
  <div v-if="availableTickets.length">
    <div class="mt-6 text-secondary">
      {{ t("labels.ppv_tickets") }}
    </div>

    <CardPPVticket
      v-for="ticket in availableTickets"
      :key="ticket.id"
      :title="ticket.title"
      :disabled="disabledCommerce"
      :price="getPriceLabel(ticket.price, ticket.currency)"
      :release-date="getReleaseDateTitle(ticket.startDate)"
      @click-buy-ticket="onClickBuyCommerce(ticket)"
    />
  </div>

  <div
    v-if="availableTickets.length && availableSubscriptions.length" role="separator"
    class="h-[1px] absolute w-full m-auto mt-6 left-0 right-0 background-popup"
  />

  <div v-if="availableSubscriptions.length">
    <div class="mt-12 text-secondary">
      {{ t("labels.subscriptions") }}
    </div>
    <CardSubscriptionNew
      v-for="subscription in availableSubscriptions" :key="subscription.id"
      class="my-6 !bg-[var(--base-tab-filter-tag-popup-bgColor)]"
      allowed-access-identity-items-class="bg-[var(--base-card-bgSecondaryColor)]"
      :title="subscription.title"
      :disabled="disabledCommerce"
      :description="subscription.description"
      :price="getPriceLabel(subscription.price, subscription.currency)"
      :duration-label="getDurationLabel(subscription)"
      :access-labels="getAccessLabels(subscription.type)"
      :release-date-title="getReleaseDateTitle(subscription.startDate)"
      :allowed-access-identity-items="subscription.allowedAccessIdentityItems"
      :region-unavailable-message="t('labels.subscription_geo_blocked_label')"
      :is-region-available="subscription.isEventAvailableInUserRegion"
      @click-subscribe="onClickBuyCommerce(subscription)"
    />
  </div>
</template>
